import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { SharedCommands } from '@project/shared.commands';
import { UserService } from 'app/modules/admin/services/user.service';
import { VersionService } from 'app/services/version.service';
import { loadMessages, locale } from 'devextreme/localization';
import * as itMessages from 'devextreme/localization/messages/it.json';
import { environment } from 'environment';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { ApiOrchestrator } from 'kiota/services/api.orchestrator';
import { ImpersonationService } from '../impersonation.service';
import { PermissionService } from '../permission.service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard extends KeycloakAuthGuard {
    constructor(
        protected readonly router: Router,
        protected readonly keycloak: KeycloakService,
        protected readonly userService: UserService,
        protected readonly api: ApiOrchestrator,
        protected readonly versionService: VersionService,
        private readonly translocoService: TranslocoService,
        private readonly permissionService: PermissionService,
        private readonly impersonationService: ImpersonationService
    ) {
        super(router, keycloak);
    }

    public async isAccessAllowed() {
        if (!this.authenticated) {
            this.router.navigate(['/supply-chain/qr-code/scanner']);
            return false;
        }

        this.api.client.api.v1.version.api.get().then((response) => {
            if (!response) return;
            if (response.version !== this.versionService.apiVersion) {
                SharedCommands.showSnackBar({
                    message: `Detected api version ${response.version}, expected ${this.versionService.apiVersion}. This could lead to unexpected behavior.`,
                    type: 'warning',
                    title: 'Wrong API version',
                    duration: 20000,
                });
            }
        });

        // Get locale from localStorage (user preference) or fallback to keycloak token
        const savedLocale = localStorage.getItem(environment.localStorageVariables.locale);
        const keycloakInstance = this.keycloak.getKeycloakInstance();
        const localeLang = savedLocale || keycloakInstance?.idTokenParsed?.locale || this.translocoService.getDefaultLang();
        localStorage.setItem(environment.localStorageVariables.locale, localeLang);

        // Save the extended locale in localStorage like it-IT
        // Get full locale code from simple locale like 'it' -> it-IT, en -> en-US
        const extendedLocale = `${localeLang}-${localeLang.toUpperCase()}`;
        localStorage.setItem(environment.localStorageVariables.extendedLocale, extendedLocale);

        if (localeLang) {
            this.translocoService.setActiveLang(localeLang);

            // DxConfig
            locale(localeLang);
            if (localeLang === 'it') {
                loadMessages(itMessages);
            }

            this.translocoService.load(localeLang).subscribe({
                error: (err) => {
                    console.error(`Errore nel caricamento della lingua ${localeLang}:`, err);
                    // Fallback alla lingua predefinita
                    this.translocoService.setActiveLang(this.translocoService.getDefaultLang());
                },
            });
        } else {
            console.warn('LocaleLang non trovato in idTokenParsed. Utilizzo della lingua predefinita.');

            // DxConfig
            locale(this.translocoService.getDefaultLang());
            if (this.translocoService.getDefaultLang() === 'it') {
                loadMessages(itMessages);
            }

            this.translocoService.setActiveLang(this.translocoService.getDefaultLang());
        }

        console.log(`Locale set to ${localeLang} (${extendedLocale})`);
        await this.userService.me();

        // Don't overwrite impersonated permissions with the real token
        if (!this.impersonationService.isImpersonating) {
            this.permissionService.loadFromToken();
        }

        return true;
    }
}
